<script lang="ts">
  import { api, ApiError, OfflineError } from '../lib/api'
  import { ripple } from '../lib/ripple'
  import Notice from '../lib/components/Notice.svelte'

  /*
   * Request account deletion — the Website's /account/delete.
   *
   * Nothing is deleted here and nothing ever will be: this raises a request that a person acts on. The
   * copy says so plainly, because "Request account deletion" and "Delete account" set very different
   * expectations about what happens in the next ten seconds.
   */

  let confirmed = $state(false)
  let busy = $state(false)
  let requested = $state(false)
  let alreadyRequested = $state(false)
  let error = $state<string | null>(null)

  async function submit(event: SubmitEvent) {
    event.preventDefault()
    if (busy || !confirmed) return
    busy = true
    error = null
    try {
      await api.requestDeletion()
      requested = true
    } catch (e) {
      // 409 means a request is already outstanding. The Website had no such check and mailed staff again
      // on every submit, so somebody revisiting the page generated a second ticket about themselves.
      if (e instanceof ApiError && e.status === 409) alreadyRequested = true
      else if (e instanceof OfflineError) error = 'Could not reach Bird Journal. Check your connection and try again.'
      else error = 'We could not send your request just now. Please try again.'
    } finally {
      busy = false
    }
  }
</script>

{#if requested}
  <Notice tone="ok" live>
    Thanks for letting us know you'd like to delete your Bird Journal account. We may be in touch to
    confirm, but will action this for you.
  </Notice>
{:else if alreadyRequested}
  <Notice tone="info" live>
    You have already asked us to delete this account, so there is nothing more to do. We may be in touch
    to confirm.
  </Notice>
{:else}
  {#if error}
    <Notice tone="error">{error}</Notice>
  {/if}

  <p>
    If you would like to delete your Bird Journal account, including all records and information it
    contains, you can request that here. We may be in touch by email to confirm, if your account contains
    a lot of records.
  </p>

  <Notice tone="warn">
    This cannot be undone. Your observations, photos and lists will be removed along with the account.
  </Notice>

  <form onsubmit={submit}>
    <label class="choice">
      <input type="checkbox" bind:checked={confirmed} />
      <span>
        <strong>I confirm</strong> that I would like to delete my Bird Journal account and all information
        it includes.
      </span>
    </label>

    <div class="btn-row">
      <button class="btn btn-danger btn-sm rip" use:ripple type="submit" disabled={busy || !confirmed}>
        {busy ? 'Sending…' : 'Request account deletion'}
      </button>
    </div>
  </form>
{/if}

<style>
  form {
    margin-top: var(--space-5);
  }
  .btn-row {
    margin-top: var(--space-5);
  }
</style>
